import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '@/store/store'
import { StripeCouponDuration, StripeSubscriptionInterval } from '@/account/store/schemas'
import { timestampMsToMonthDayOrdinal } from '@/util/time'
import { formatCentsToDollarsCompact } from '@/util/money'

/**
 * Selector: Has Forever Percent Discount
 *
 * Returns true if the applied coupon is:
 * - duration = forever
 * - percent_off is a positive number
 */
export const selectHasForeverPercentDiscount = createSelector(
  (state: RootState) => state.accountState.appliedCoupon,
  (coupon) => Boolean(coupon && coupon.duration === StripeCouponDuration.FOREVER && typeof coupon.percent_off === 'number' && coupon.percent_off > 0)
)

/**
 * Selector: Display Cost Per Seat In Cents
 *
 * Returns the cost per seat in cents to display to the user. If the user has a forever percentage discount,
 * applies the percent_off to the standard per seat cost. Otherwise returns the standard per seat cost.
 *
 * Note: Amount-off coupons and non-forever percent discounts are intentionally ignored here.
 * - Amount-off discounts can't be reliably broken down into a per-seat price because the total
 *   discount is applied at the invoice level and may vary based on the number of seats.
 * - Non-forever percent discounts are also excluded to avoid misleading users with a temporary
 *   price that will increase once the promotion expires.
 */
export const selectDisplayCostPerSeatInCents = createSelector(
  (state: RootState) => state.accountState.costPerSeatInCents,
  selectHasForeverPercentDiscount,
  (state: RootState) => state.accountState.appliedCoupon,
  (costPerSeat, hasForeverPercent, coupon) => {
    if (costPerSeat == null) return null

    // Just return the standard per-seat cost if there is no forever percent discount
    if (!hasForeverPercent || !coupon?.percent_off) return costPerSeat

    const discounted = Math.round(costPerSeat * (1 - coupon.percent_off / 100))
    return Math.max(0, discounted)
  }
)

/**
 * Selector: Active Trial Ending Message
 *
 * Returns the message to display for the ACTIVE_TRIAL variant of the subscription notice banner.
 * Includes graceful fallbacks in place in case any of the billing details are unavailable.
 */
export const selectActiveTrialEndingMessage = createSelector(
  (state: RootState) => state.accountState.initialLoadComplete,
  (state: RootState) => state.accountState.trialEnd,
  (state: RootState) => state.accountState.upcomingInvoiceAmountDueInCents,
  (state: RootState) => state.accountState.stripeSubscriptionInterval,
  (state: RootState) => state.accountState.numberOfSeats,
  (state: RootState) => state.accountState.hasDiscount,
  selectHasForeverPercentDiscount,
  selectDisplayCostPerSeatInCents,

  (initialLoadComplete, trialEnd, upcomingInvoiceAmountDue, subscriptionInterval, numberOfSeats, hasDiscount, hasForeverPercentDiscount, costPerSeat) => {
    // If the initial load is not complete, the data is not available so return an empty string
    if (!initialLoadComplete) return ''

    // Trial end date
    const formattedTrialEnd = trialEnd ? `on ${timestampMsToMonthDayOrdinal(trialEnd)}` : 'at the end of your trial'

    // Subscription interval (for renewal wording)
    const formattedSubscriptionInterval =
      subscriptionInterval === StripeSubscriptionInterval.YEAR
        ? 'annual'
        : subscriptionInterval === StripeSubscriptionInterval.MONTH
        ? 'monthly'
        : 'subscription' // fallback

    // If upcomingInvoiceAmountDue is null (data is missing), don't risk showing "will be charged" wording
    if (upcomingInvoiceAmountDue == null) {
      return `Your ${formattedSubscriptionInterval} plan will renew on ${formattedTrialEnd}.`
    }

    // Amount that will be charged - can be $0 if there are promos, discounts, or credits applied
    const formattedUpcomingInvoiceAmountDue = formatCentsToDollarsCompact(upcomingInvoiceAmountDue)

    // Number of seats and cost per seat (for organizations)
    const formattedNumberOfSeats = numberOfSeats && numberOfSeats > 1 ? `${numberOfSeats} seats` : ''
    const formattedCostPerSeat = costPerSeat && costPerSeat > 0 ? `${formatCentsToDollarsCompact(costPerSeat)}` : ''
    const formattedSeatCost = formattedNumberOfSeats && formattedCostPerSeat ? `${formattedCostPerSeat} x ${formattedNumberOfSeats}` : ''

    // When a non-forever percent discount is applied, the per-seat cost may not reflect the actual amount charged.
    // To avoid confusion, we only show the number of seats and hide the per-seat pricing.
    const hasNonForeverPercentDiscount = hasDiscount && !hasForeverPercentDiscount

    const seatInfo = hasNonForeverPercentDiscount ? formattedNumberOfSeats : formattedSeatCost
    const formattedSeatInfo = seatInfo ? `(${seatInfo})` : ''

    return `The card on file will be charged ${formattedUpcomingInvoiceAmountDue} ${formattedTrialEnd} for your ${formattedSubscriptionInterval} renewal. ${formattedSeatInfo}`
  }
)

/**
 * Selector: In Trial Canceled Message
 *
 * Returns the message to display for the IN_TRIAL_CANCELED variant of the subscription notice banner.
 */
export const selectInTrialCanceledMessage = createSelector(
  (state: RootState) => state.accountState.initialLoadComplete,
  (state: RootState) => state.accountState.trialEnd,
  (initialLoadComplete, trialEnd) => {
    return initialLoadComplete && trialEnd
      ? `You will lose access to Paxton when your trial ends on ${timestampMsToMonthDayOrdinal(trialEnd)}.`
      : `You will lose access to Paxton at the end of your trial.`
  }
)
